// Batch 27 · Add-on 2 Step 2 (owner, 16-Oct-2026) — ONE banner for every analysis whose result is stored and run on the
// user's click: "Not run yet — Run", "Data changed since the last run — Rescan", "Last run DD-MMM-YYYY HH:mm by <name>",
// and a running state with progress. EN / AR. The analyses keep computing as before; only how their run state is shown is
// shared.
import React from "react";
import { Button } from "@/components/ui/button";
import { fmtDateTimeFirm } from "@/lib/dt";   // follow-up 5d: the firm time zone (DD-MMM-YYYY HH:mm), as Analytics did

export const SRB_TEXT = {
  en: { notRun: "Not run yet", run: "Run", stale: "Data changed since the last run", rescan: "Rescan",
        lastRun: "Last run", by: "by", running: "Running…", failed: "The last run failed" },
  ar: { notRun: "لم يُشغَّل بعد", run: "تشغيل", stale: "تغيّرت البيانات منذ آخر تشغيل", rescan: "إعادة الفحص",
        lastRun: "آخر تشغيل", by: "بواسطة", running: "قيد التشغيل…", failed: "فشل آخر تشغيل" },
};

/**
 * state: "not_run" | "stale" | "fresh"; runAt (ISO); runBy (name); running (bool); progress (0–100); message; error;
 * onRun(): starts the job (the parent follows it); canRun: false hides the button (no rights); runTestId: the screen's own
 * test id for the Run / Rescan button (the acceptance driver clicks it); children: extra detail on the same line.
 */
export default function StoredResultBanner({ lang = "en", state = "not_run", runAt = "", runBy = "", running = false,
  progress = 0, message = "", error = "", onRun, canRun = true, testid = "stored-result-banner", runTestId = "",
  runDisabled = false, children = null }) {
  const L = SRB_TEXT[lang === "ar" ? "ar" : "en"];
  const tone = running ? "border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/30 dark:text-sky-100"
    : state === "fresh" && !error ? "border-slate-200 bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
      : "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100";
  const label = running ? L.running
    : error ? L.failed
      : state === "not_run" ? L.notRun
        : state === "stale" ? L.stale
          : `${L.lastRun} ${fmtDateTimeFirm(runAt)}${runBy ? ` ${L.by} ${runBy}` : ""}`;
  const btn = state === "not_run" ? L.run : L.rescan;
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-xs ${tone}`} data-testid={testid}
         data-state={running ? "running" : state} role="status">
      <span data-testid={`${testid}-label`}>{label}</span>
      {state === "stale" && !running && runAt && (
        <span className="text-[11px] opacity-80" data-testid={`${testid}-last`}>
          · {L.lastRun} {fmtDateTimeFirm(runAt)}{runBy ? ` ${L.by} ${runBy}` : ""}
        </span>
      )}
      {running && (
        <span className="text-[11px]" data-testid={`${testid}-progress`}>{Math.round(progress || 0)}%{message ? ` · ${message}` : ""}</span>
      )}
      {error && !running && <span className="text-[11px]" data-testid={`${testid}-error`}>{error}</span>}
      {children}
      {canRun && onRun && !running && (
        <Button size="sm" variant={state === "fresh" ? "outline" : "default"} className="ms-auto h-7" onClick={onRun}
                disabled={runDisabled} data-testid={runTestId || `${testid}-run`}>{btn}</Button>
      )}
    </div>
  );
}
